import React, { useEffect, useState } from "react";
import axios from "axios";
import { Crown, Trophy, Loader2, Sparkles } from "lucide-react";
import { CLASSES } from "../../data/classes";
import { BORDERS_BY_ID } from "../../data/cosmetics";
import { getRank } from "../../data/achievements";
import { useSocial } from "../../contexts/SocialContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDAL = ["🥇", "🥈", "🥉"];

export const Leaderboard = ({ limit = 10 }) => {
  const { openProfile } = useSocial();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/leaderboard/ranked?limit=${limit}`);
        if (mounted) setEntries(res.data.entries || []);
      } catch (e) {
        if (mounted) setEntries([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [limit]);

  return (
    <div data-testid="leaderboard" className="bg-slate-800 border-4 border-slate-950 rounded-3xl p-4 cartoon-shadow-lg-static w-full">
      <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2 text-yellow-400">
        <Trophy className="w-5 h-5" /> Classement PvP — Top {limit}
      </h2>
      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400 gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>
      ) : entries.length === 0 ? (
        <p data-testid="leaderboard-empty" className="text-slate-400 text-sm italic text-center py-4">
          Personne n'a encore joué en classé. Sois le premier à grimper !
        </p>
      ) : (
        <ol className="space-y-1.5">
          {entries.map((e) => {
            const rankInfo = getRank(e.rank_points);
            const cls = e.avatar_classe ? CLASSES[e.avatar_classe] : null;
            const border = BORDERS_BY_ID[e.equipped_border || "default"] || BORDERS_BY_ID.default;
            return (
              <li
                key={e.user_id}
                data-testid={`lb-entry-${e.rank}`}
                onClick={() => openProfile(e.user_id)}
                role="button"
                className={`flex items-center gap-2 ${e.rank <= 3 ? "bg-slate-900" : "bg-slate-900/60"} border-2 border-slate-950 rounded-xl p-2 cursor-pointer hover:border-yellow-500 transition-colors`}
              >
                <span className="w-8 text-center font-display font-bold text-yellow-400">
                  {MEDAL[e.rank - 1] || `#${e.rank}`}
                </span>
                <div className={`w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 ${border.style.className}`}>
                  {cls ? (
                    <div className={`w-full h-full bg-gradient-to-br ${cls.couleur}`}>
                      <img src={cls.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : e.picture ? (
                    <img src={e.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-700" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-sm text-white truncate">{e.name}</p>
                  <p className={`text-[10px] ${rankInfo.current.text === "text-white" ? "text-slate-300" : "text-yellow-400"} font-bold`}>
                    {rankInfo.current.label} · Niv. {e.level}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-yellow-400 text-sm flex items-center justify-end gap-1">
                    <Crown className="w-3 h-3" /> {e.rank_points}
                  </p>
                  <p className="text-[10px] text-slate-500">pts</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
